<template>
  <section class="dash-summary-grid" aria-label="整体指标概览">
    <div
      v-for="(group, groupIndex) in groupedCards"
      :key="groupIndex"
      class="dash-summary-group"
    >
      <article
        v-for="card in group"
        :key="card.label"
        :class="['dash-summary-card', { 'dash-summary-card--syncing': card.syncing }]"
      >
        <span class="dash-summary-card__label">{{ card.label }}</span>
        <strong class="dash-summary-card__value">{{ card.value }}</strong>
        <p class="dash-summary-card__note">
          <span v-if="card.syncing" class="dash-summary-card__pulse" aria-hidden="true"></span>
          {{ card.note }}
        </p>
      </article>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getGroupKpi } from '@/api'

const props = defineProps({
  campPeriodName: { type: String, default: '' },
  orgUnitId: { type: String, default: '' },
})

const kpi = ref({
  teacherCount: 0,
  allocatedCount: 0,
  overallReachRate: 0,
  callReachRate: 0,
  avgCallDuration: 0,
  anomalyRate: 0,
  violation: 0,
  qcSummaryStatus: 'PARTIAL',
  qcSummaryLabel: '数据同步中',
})

const qcSummaryNote = computed(() => (
  kpi.value.qcSummaryLabel ||
  (kpi.value.qcSummaryStatus === 'COMPLETE'
    ? '已包含通话与聊天质检'
    : '数据同步中')
))

const cards = computed(() => [
  { label: '指导师人数', value: `${kpi.value.teacherCount}`, note: '活跃服务中' },
  { label: '分配数据量', value: `${kpi.value.allocatedCount}`, note: '本周期分配' },
  { label: '整体触达率', value: `${kpi.value.overallReachRate}%`, note: '家长综合触达' },
  { label: '通话触达率', value: `${kpi.value.callReachRate}%`, note: 'VOIP 通话覆盖' },
  { label: '平均通话时长', value: `${kpi.value.avgCallDuration} 分钟`, note: '深聊时长' },
  {
    label: '异常违规率',
    value: `${kpi.value.violation ?? kpi.value.anomalyRate ?? 0}%`,
    note: qcSummaryNote.value,
    syncing: kpi.value.qcSummaryStatus !== 'COMPLETE',
  },
])

const groupedCards = computed(() => [
  cards.value.slice(0, 2),
  cards.value.slice(2, 4),
  cards.value.slice(4, 6),
])

async function fetchKpi() {
  if (!props.campPeriodName) return
  try {
    const params = { campPeriodName: props.campPeriodName }
    if (props.orgUnitId) params.orgUnitId = props.orgUnitId
    const res = await getGroupKpi(params)
    if (res.code === 200 && res.data) {
      kpi.value = res.data
    } else if (res.teacherCount !== undefined) {
      kpi.value = res
    }
  } catch (err) {
    console.error('获取 KPI 数据失败', err)
  }
}

watch(() => [props.campPeriodName, props.orgUnitId], fetchKpi, { immediate: true })
</script>

<style scoped>
.dash-summary-grid {
  display: grid;
  grid-template-columns: var(--dash-main-columns);
  gap: 10px;
}

.dash-summary-group {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.dash-summary-card {
  position: relative;
  overflow: hidden;
  padding: 10px 12px;
}

.dash-summary-card--syncing {
  border-color: rgba(28, 92, 100, 0.18);
}

.dash-summary-card__note {
  display: flex;
  align-items: center;
  gap: 5px;
}

.dash-summary-card__pulse {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #d89b36;
  box-shadow: 0 0 0 0 rgba(216, 155, 54, 0.35);
  animation: qc-sync-pulse 1.8s ease-out infinite;
  flex: 0 0 auto;
}

@keyframes qc-sync-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(216, 155, 54, 0.35);
  }
  70% {
    box-shadow: 0 0 0 7px rgba(216, 155, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(216, 155, 54, 0);
  }
}

@media (max-width: 1360px) {
  .dash-summary-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .dash-summary-grid {
    grid-template-columns: 1fr;
  }

  .dash-summary-group {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .dash-summary-card {
    padding: 8px 10px;
  }

  .dash-summary-card__value {
    font-size: 1.1rem;
  }

  .dash-summary-card__note {
    font-size: 0.68rem;
  }
}
</style>
